import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { toConvexId } from "@/types/convex";

export const useUpdateDeviceRules = () => {
  const queryClient = useQueryClient();
  const updateDeviceRules = useConvexMutation(api.devices.rules.update);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      deviceId, 
      ruleId,
      enabled,
      currentRules = {},
    }: { 
      deviceId: string; 
      ruleId: string;
      enabled: boolean;
      currentRules?: Record<string, boolean>;
    }) => {
      // Validate inputs
      if (!deviceId || typeof deviceId !== 'string') {
        throw new Error('Invalid device ID provided');
      }
      if (!ruleId || typeof ruleId !== 'string') {
        throw new Error('Invalid rule ID provided');
      }

      // Merge current rules with the change - send full state to backend
      const rules: Record<string, boolean> = {
        ...currentRules,
        [ruleId]: enabled,
      };

      console.log('useUpdateDeviceRules - Payload:', { 
        device: deviceId, 
        rules,
        action: enabled ? 'ADD' : 'REMOVE'
      });

      const result = await updateDeviceRules({
        device: toConvexId.device(deviceId),
        rules: rules as any,
      });

      console.log('useUpdateDeviceRules - Device rule updated successfully:', result);
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['network-rules'] });
      queryClient.invalidateQueries({ queryKey: ['device-rules', variables.deviceId] });
      
      toast({
        title: variables.enabled ? "Rule Added" : "Rule Removed",
        description: `Network rule has been ${variables.enabled ? 'added to' : 'removed from'} this device.`,
      });
    },
    onError: (error) => {
      console.error('useUpdateDeviceRules - Error updating device rule:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update device rule. Please try again.",
        variant: "destructive",
      });
    }
  });
};
